import { useContext, useMemo, useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import AIBotTab_5 from "@/pages/cbb-builder/components/AIBotTab_5";
import { BotContext } from "@/pages/cbb-builder/store";
import type { State } from "@/redux/Types";
import useBotSave from "@/pages/cbb-builder/react-query/useBotSave";
import { axiosGet } from "@/services/axiosService";
const setNestedValue = (
  data: Record<string, any>,
  path: string,
  value: any,
) => {
  const keys = path.split(".");
  const nextData = { ...data };
  let current = nextData;

  keys.forEach((key, index) => {
    if (index === keys.length - 1) {
      current[key] = value;
      return;
    }

    current[key] = { ...(current[key] ?? {}) };
    current = current[key];
  });

  return nextData;
};

const BotIntelligenceSetup = () => {
  const [store, storeDispatch] = useContext(BotContext);
  const { pathname } = useLocation();
  const isFreshSignupOnboarding = pathname.replace(/\/$/, "") === "/onboarding";
  const plan = useSelector((state: State) => state.user.plan);
  const { mutateAsync: saveBot, status: updateStatus } = useBotSave();
  const companyDetails: any = useSelector(
    (state: State) => state.user.companyDetails,
  );

  const allowedModels = useMemo(
    () => companyDetails?.ai_currentLimits?.allowedModels ?? [],
    [companyDetails?.ai_currentLimits?.allowedModels],
  );

  const hasFetchedRef = useRef(false);
  const botDataRef = useRef(store.botData);
  botDataRef.current = store.botData;

  useEffect(() => {
    if (hasFetchedRef.current) return;

    const fetchCompleteBot = async () => {
      const botUrl = botDataRef.current?.url;
      if (!botUrl || botDataRef.current?.prompts?.length) return;

      hasFetchedRef.current = true;

      try {
        const response = await axiosGet(`/users/completeBot?url=${botUrl}`);
        if (response.data.success && response.data.data) {
          storeDispatch({
            type: "set-botData",
            botData: {
              ...botDataRef.current,
              ...response.data.data,
            },
          });
        }
      } catch (err) {
        console.error("Error fetching complete bot in onboarding:", err);
      }
    };

    fetchCompleteBot();
  }, []);

  const handleDataChange = (field: string, value: any) => {
    const botData =
      field === "root"
        ? value
        : setNestedValue(store.botData ?? {}, field, value);

    storeDispatch({
      type: "set-botData",
      botData,
    });
    saveBot(botData);
  };

  return (
    <div className="min-h-[520px] w-full overflow-hidden bg-white">
      <AIBotTab_5
        botData={store.botData}
        onDataChange={handleDataChange}
        setSelectedSetupTab={() => {}}
        allowedModels={allowedModels}
        plan={plan}
        defaultByokConfigId={companyDetails?.defaultByokConfigId}
        onTryNow={() => {}}
        confirmTryNowNavigation
        hideApiKeyOption={false}
        hideByokOptions={isFreshSignupOnboarding}
        hideAdvancedSettings
        hideModelQualities
      />
    </div>
  );
};

export default BotIntelligenceSetup;
